<template>
    <div>
        <page-header>
            {{ $t('finance.title') }}
        </page-header>

        <error-message :value="commissionsError" />
        <error-message :value="userProfileError" />

        <div class="row">
            <div class="col-xl-4 col-md-6 mb-4">
                <WidgetStat :title="$t('finance.newCommission')" :value="commissionsFormatted.openFormatted" :loading="fetchCommissionsLoading" />
            </div>
            <div class="col-xl-4 col-md-6 mb-4">
                <WidgetStat :title="$t('finance.acceptedCommission')" :value="commissionsFormatted.acceptedFormatted" :loading="fetchCommissionsLoading" />
            </div>
            <div class="col-xl-4 col-md-6 mb-4">
                <WidgetStat :title="$t('finance.toPayCommission')" :value="commissionsFormatted.toPaidFormatted" :loading="fetchCommissionsLoading">
                    <router-link v-if="isPaymentAvailable && user.isCompany === true" :to="{ name: 'PaymentGenerator' }">
                        <ButtonIcon class="ml-2" variant="success" short>
                            {{ $t('finance.payOut') }}
                        </ButtonIcon>
                    </router-link>
                    <ButtonIcon v-if="isPaymentAvailable && user.isCompany === false" class="ml-2" variant="success" short @click="handlePayOutPrivate">
                        {{ $t('finance.payOut') }}
                    </ButtonIcon>
                    <payment-generator-private v-if="isPaymentAvailable && user.isCompany === false" v-model="privateGeneratorModalVisible" @done="handlePayOutComplete" />
                </WidgetStat>
            </div>
        </div>

        <div class="row">
            <div class="col-12 mb-4">
                <FinanceChart :loading="fetchFinanceDocumentsLoading" :error="financeDocumentsError" :series="financeChartData" />
            </div>
        </div>

        <space-provider>
            <card :header="$t('finance.paymentHistory')" :loading="fetchFinanceDocumentsLoading">
                <error-message v-model="financeDocumentsError" />
                <DataTable :items="financeDocumentsItems" :fields="fields" :pagination="false">
                    <template #cell(createdAt)="{ item }">
                        {{ $format.date(item.createdAt) }}
                    </template>
                    <template #cell(net)="{ item }">
                        {{ $format.currency(item.net) }}
                    </template>
                    <template #cell(number)="{ item }">
                        {{ item.number }}
                        <button-icon
                            v-if="canDownload(item)"
                            :disabled="!!isDownloadingDocument"
                            :loading="isDownloadingDocument === item.id"
                            class="float-right"
                            size="sm"
                            variant="link"
                            icon="file-text"
                            @click="downloadDocument(item)"
                        />
                    </template>
                    <template #cell(paymentDate)="{ item }">
                        {{ $format.date(item.paymentDate) }}
                        <b-progress v-if="item.paymentDateProgressType" :value="item.paymentDateProgress" :max="paymentTimeDays" :variant="item.paymentDateProgressType" />
                    </template>
                    <template #cell(status)="{ item }">
                        <BadgeFinanceDocumentStatus :status="item.status" />
                    </template>
                    <template #cell(transactions)="{ item }">
                        <router-link
                            v-if="hasTransactions(item)"
                            :to="{
                                name: 'ReportsTransactions',
                                query: { financeDocuments: item.id },
                            }"
                        >
                            {{ $t('finance.showTransactions') }}
                        </router-link>
                    </template>
                </DataTable>
            </card>
        </space-provider>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';
    import { startOfMonth, getTime, addDays, differenceInDays } from 'date-fns';
    import { FinanceDocumentStatus, PAYOUT_THRESHOLD, translateFinanceDocumentStatus } from '@/services/financeService';
    import { generateEChartData } from '@/utils/chart';
    import PageHeader from '@/components/common/PageHeader.vue';
    import Card from '@/components/common/Card.vue';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import SpaceProvider from '@/components/common/SpaceProvider.vue';
    import WidgetStat from '@/components/common/WidgetStat.vue';
    import ButtonIcon from '@/components/common/ButtonIcon.vue';
    import exportService from '@/services/exportService';
    import BadgeFinanceDocumentStatus from '@/components/common/BadgeFinanceDocumentStatus.vue';
    import FinanceChart from './parts/FinanceChart.vue';
    import PaymentGeneratorPrivate from './parts/PaymentGeneratorPrivate.vue';

    export default {
        name: 'Finance',
        components: {
            SpaceProvider,
            PaymentGeneratorPrivate,
            BadgeFinanceDocumentStatus,
            DataTable,
            ErrorMessage,
            PageHeader,
            Card,
            WidgetStat,
            FinanceChart,
            ButtonIcon,
        },
        data() {
            return {
                paymentTimeDays: 21,
                commissionsError: null,
                financeDocumentsError: null,
                userProfileError: null,
                privateGeneratorModalVisible: false,
                isCompany: null,
                isDownloadingDocument: false,
            };
        },
        computed: {
            fields() {
                return [
                    { key: 'createdAt', label: this.$t('finance.date') },
                    { key: 'number', label: this.$t('finance.documentNumber') },
                    { key: 'paymentDate', label: this.$t('finance.paymentDate') },
                    {
                        key: 'net',
                        label: this.$t('finance.amount'),
                        tdClass: 'text-right',
                        thClass: 'text-right',
                    },
                    {
                        key: 'status',
                        label: this.$t('finance.status'),
                        tdClass: 'text-center',
                        thClass: 'text-center',
                    },
                    {
                        key: 'transactions',
                        label: this.$t('finance.transactions'),
                        tdClass: 'text-center',
                        thClass: 'text-center',
                    },
                ];
            },
            commissionsFormatted() {
                if (!this.commissions) return {};
                return {
                    ...this.commissions,
                    openFormatted: this.$format.currency(this.commissions.open),
                    acceptedFormatted: this.$format.currency(this.commissions.accepted),
                    toPaidFormatted: this.$format.currency(this.commissions.toPaid),
                };
            },
            financeDocumentsItems() {
                const now = new Date();
                return this.financeDocuments
                    .map(item => {
                        const paymentDate = addDays(item.createdAt, this.paymentTimeDays);
                        const daysLeft = differenceInDays(paymentDate, now);
                        let paymentDateProgress;
                        let paymentDateProgressType;

                        if (![FinanceDocumentStatus.REJECTED, FinanceDocumentStatus.PAID].includes(item.status.toString())) {
                            if (daysLeft < 0) {
                                paymentDateProgress = 100;
                                paymentDateProgressType = 'danger';
                            } else {
                                paymentDateProgress = differenceInDays(now, item.createdAt);
                                paymentDateProgressType = 'success';
                            }
                        }

                        return {
                            ...item,
                            statusFormatted: translateFinanceDocumentStatus(item.status.toString()),
                            paymentDate,
                            paymentDateProgress,
                            paymentDateProgressType,
                        };
                    })
                    .reverse();
            },
            isPaymentAvailable() {
                return this.commissions && this.commissions.toPaid >= PAYOUT_THRESHOLD && !this.fetchUserProfileLoading && !this.userProfileError && this.user;
            },
            financeChartData() {
                if (!this.financeDocuments?.length) return [];

                const sortedDocuments = [...this.financeDocuments].sort((documentA, documentB) => new Date(documentA.createdAt) - new Date(documentB.createdAt));

                const monthlyAggregated = sortedDocuments.reduce((monthlyAccumulator, documentItem) => {
                    if (!this.hasTransactions(documentItem)) {
                        return monthlyAccumulator;
                    }

                    const monthTimestamp = getTime(startOfMonth(new Date(documentItem.createdAt)));
                    const documentValue = documentItem.net;

                    const existingMonth = monthlyAccumulator.find(monthEntry => monthEntry.date === monthTimestamp);

                    if (existingMonth) {
                        existingMonth.value += documentValue;
                    } else {
                        monthlyAccumulator.push({
                            date: monthTimestamp,
                            value: documentValue,
                        });
                    }

                    return monthlyAccumulator;
                }, []);

                if (!monthlyAggregated.length) return [];

                const timeRange = {
                    startDate: new Date(sortedDocuments[0].createdAt),
                    endDate: new Date(),
                };

                const rawPairs = monthlyAggregated.map(({ date, value }) => [date, value]);
                return generateEChartData(timeRange, rawPairs);
            },
            ...mapState({
                fetchCommissionsLoading: state => state.finance.loading.fetchCommissions,
                fetchFinanceDocumentsLoading: state => state.finance.loading.fetchFinanceDocuments,
                commissions: state => state.finance.commissions,
                financeDocuments: state => state.finance.financeDocuments || [],
                user: state => state.auth.user,
                fetchUserProfileLoading: state => state.auth.loading.fetchUserProfile,
            }),
        },
        methods: {
            async getCommissions() {
                try {
                    this.commissionsError = null;
                    await this.fetchCommissions();
                } catch (e) {
                    this.commissionsError = e.message;
                }
            },
            async getFinanceDocuments() {
                try {
                    this.financeDocumentsError = null;
                    await this.fetchFinanceDocuments();
                } catch (e) {
                    this.financeDocumentsError = e.message;
                }
            },
            async getAccountSettings() {
                try {
                    this.userProfileError = null;
                    await this.fetchUserProfile();
                } catch (e) {
                    this.userProfileError = e.message;
                }
            },
            hasTransactions(item) {
                return [FinanceDocumentStatus.NEW, FinanceDocumentStatus.ACCEPTED, FinanceDocumentStatus.PAID].includes(item.status.toString());
            },
            handlePayOutPrivate() {
                this.privateGeneratorModalVisible = true;
            },
            handlePayOutComplete() {
                this.getCommissions();
                this.getFinanceDocuments();
            },
            canDownload(item) {
                return item.downloadable && this.user.isCompany === false;
            },
            async downloadDocument(item) {
                try {
                    this.isDownloadingDocument = item.id;
                    await exportService.exportDataAuthorized(`${process.env.VUE_APP_API_URL}/api/v1/finance-documents/${item.id}/download`, item.number, 'pdf');
                } catch (e) {
                    this.$toastr.error(e.message);
                } finally {
                    this.isDownloadingDocument = null;
                }
            },
            ...mapActions({
                fetchCommissions: 'finance/fetchCommissions',
                fetchFinanceDocuments: 'finance/fetchFinanceDocuments',
                fetchUserProfile: 'auth/fetchUserProfile',
            }),
        },
        created() {
            this.getCommissions();
            this.getFinanceDocuments();
            this.getAccountSettings();
        },
    };
</script>

<style lang="scss" scoped>
    .commission-value {
        margin-bottom: 0.25rem;
        font-size: 28px;
    }
    .commission-label {
        font-size: 18px;
        color: $text-muted;
    }
</style>
